import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor } from '@repo/tailwind-config/tailwind.vars'
import { PAGINATION_LIMIT } from '@repo/toolkit/constants/pagination'
import { SVGFC, Version } from '@repo/toolkit/types'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button, { ButtonVariant } from '@repo/ui/button'
import Counter from '@repo/ui/counter'
import DataTable, {
  Column,
  DataTableSelectedData,
  DataTableSelectMode,
  ExpandedDataTableOptions,
  Legend,
  PaginationInput,
  PaginationRequest,
  PaginationResponse,
  TableRequiredFields,
  TimelineData
} from '@repo/ui/data-table'
import { CheckMarkIcon, DeleteIcon, FilterIcon, SearchIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react'
import { toast } from 'sonner'

import { restApiClient } from '../../../api/rest-api-client'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { useUpdateEffect } from '../../../lib/hooks/use-update-effect'
import { useUrl } from '../../../lib/hooks/use-url'
import {
  createSortingObject,
  encodeNestedObject
} from '../../../lib/utils/functions/misc.functions'
import { DeleteMessage, getDeleteMessage } from './utils/deleteMessage'

// Define a type that includes the generics and ref attributes
type DynamicDataTableComponent = <
  TData extends TableRequiredFields,
  TFilters = Record<string, unknown>,
  TExpanded extends TableRequiredFields | undefined = undefined,
  TAvailableVersions extends string = Version,
  TVersion extends TAvailableVersions = TAvailableVersions
>(
  props: DynamicDataTableProps<TData, TFilters, TExpanded, TAvailableVersions, TVersion> &
    React.RefAttributes<DynamicTableRef<TData>>
) => React.ReactElement | null

type ColumnSort<TData> = {
  // Nested columns (accessorKey 'a.b') get the column id 'a_b'
  id: (keyof TData & string) | `${keyof TData & string}_${string}`
  desc: boolean
}

export interface DynamicTableRef<TData = unknown> {
  currentData?: TData[]
  refetch: () => void
  startSelectAllLoading: () => void
  endSelectAllLoading: () => void
  clearSelection: () => void
  setSelectedRows(rows: TData[]): void
}

export type TableAction<TData, TExpanded> = {
  type?: 'global' | 'multiple'
  label: string
  icon?: SVGFC
  variant?: ButtonVariant
  disabled?:
    | boolean
    | ((selectedRows: TData[], expandedRows?: TExpanded[], allData?: TData[]) => boolean)
  hasAccess?: boolean
  requiredConfirmation?: boolean
  confirmationIcon?: SVGFC
  confirmationMessage?: string
  refetch?: boolean
  successMessage?: string
  confirmText?: string
  hideButtonIcon?: boolean
  cancelText?: string
  onCancel?: () => void
  fn?: (rows: TData[], expandedRows?: TExpanded[]) => unknown
}

type TablePermissions<TData> = {
  delete?: boolean
  disableDelete?: (data: TData[]) => boolean
}

export type HeaderType = 'full' | 'light' | 'none' | 'minimal' | 'actions-only' | 'hide-total'

export type DynamicDataTableProps<
  TData,
  TFilter,
  TExpanded extends TableRequiredFields | undefined,
  TAvailableVersions extends string = Version,
  TVersion extends TAvailableVersions = TAvailableVersions
> = {
  title?: string
  description?: string
  label?: (count: number) => ReactNode
  endpoint: string
  displayOrder?: boolean | 'before-select'
  footer?: React.ReactNode
  footerclassName?: string
  searchLabel?: string
  limitControl?: boolean
  columns: Column<TData>[]
  selectable?: boolean
  disableSelectAll?: boolean
  selectMode?: DataTableSelectMode
  paginated?: boolean
  legends?: Legend[]
  expandedOptions?: ExpandedDataTableOptions<TData, TExpanded>
  permissions?: TablePermissions<TData>
  filters?: TFilter
  sorting?: ColumnSort<TData>
  headerInfo?: React.ReactNode
  filtersLength?: number
  headerType?: HeaderType
  mode?: 'normal' | 'timeline'
  specialRows?: {
    borderColor?: TailwindColor
    color?: TailwindColor
    condition: (row: TData) => boolean
  }[]
  overflow?: boolean
  widget?: React.ReactNode | ((data: TData[]) => React.ReactNode)
  isSearchable?: boolean
  className?: string
  tableClassName?: string
  version?: TVersion
  deleteMessage?: string | DeleteMessage<TData>
  bodyVariant?: 'regular' | 'white'
  // Allow to use the search param in the URL to store the values (search, page, limit, sortBy, filters)
  // This is useful when we want to keep the values in the URL
  allowSearchParam?: boolean
  actionLabel?: ReactNode
  footerCells?: {
    accessorKey: Column<TData>['accessorKey']
    value: React.ReactNode
    colSpan?: number
  }[]
  postActions?: ReactNode
  selectedData?: DataTableSelectedData<TData, TExpanded>
  onDataChange?: (data: TData[]) => void
  onLoadingChange?: (loading: boolean) => void
  onFiltersClick?: () => void
  onRowClick?: (row: TData, event?: React.MouseEvent) => void
  disableRow?: (row: TData) => boolean
  onSearchChange?: (search: string) => void
  onSelectedDataChange?: (input: DataTableSelectedData<TData, TExpanded>) => void
  onDeleteSuccess?: (rows: TData[]) => unknown
  extraParams?: Record<string, unknown>
} & (
  | {
      mode?: 'normal'
      actions?: TableAction<TData, TExpanded>[]
    }
  | {
      mode: 'timeline'
      actions?: TableAction<TimelineData<TData> | TData, TExpanded>[]
    }
)

export const DynamicDataTableTitle = ({ children }: PropsWithChildren) => (
  <Typography variant="sub-heading">{children}</Typography>
)

export const DynamicDataTableSearchInput: React.FC<{
  label?: string
  value: string
  headerType: HeaderType
  onChange: (value: string) => void
}> = ({ label, value, headerType, onChange }) => {
  return (
    <Input
      options={{ clear: true }}
      type="text"
      icon={SearchIcon}
      containerClassName={cn({
        'w-full': headerType === 'light',
        'w-64': headerType === 'full' || headerType === 'hide-total',
        hidden: headerType === 'minimal' || headerType === 'actions-only'
      })}
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

const DynamicDataTable = React.forwardRef<
  DynamicTableRef<never>,
  DynamicDataTableProps<never, never, never, never, never>
>(function DynamicDataTable<
  TData extends TableRequiredFields,
  TFilters = Record<string, unknown>,
  TAvailableVersions extends string = Version,
  TVersion extends TAvailableVersions = TAvailableVersions,
  TExpanded extends TableRequiredFields | undefined = undefined
>(
  {
    className,
    title,
    label,
    description,
    searchLabel,
    endpoint,
    displayOrder = false,
    columns,
    footer,
    footerclassName,
    paginated = true,
    bodyVariant,
    selectable,
    disableSelectAll,
    selectMode,
    limitControl = true,
    expandedOptions,
    actions = [],
    actionLabel,
    postActions,
    permissions = { delete: true },
    version,
    specialRows,
    filters,
    headerInfo,
    isSearchable = true,
    deleteMessage,
    allowSearchParam,
    filtersLength,
    headerType = 'full',
    mode = 'normal',
    legends,
    footerCells,
    overflow,
    tableClassName,
    widget,
    sorting,
    selectedData: intialSelectedData,
    onSelectedDataChange,
    onDeleteSuccess,
    onRowClick,
    disableRow,
    onLoadingChange,
    onFiltersClick,
    onSearchChange,
    onDataChange,
    extraParams
  }: DynamicDataTableProps<TData, TFilters, TExpanded, TAvailableVersions, TVersion>,
  ref: React.Ref<DynamicTableRef<TData>>
) {
  const { pushPagination, readPagination } = useUrl()
  const { t } = useTranslate()
  const memoizedFilters = useMemo(() => filters, [JSON.stringify(filters)])
  const memoizedExtraParams = useMemo(() => extraParams, [JSON.stringify(extraParams)])

  const {
    page = 0,
    limit = PAGINATION_LIMIT,
    sortBy,
    q = ''
  } = useMemo(
    () =>
      allowSearchParam
        ? readPagination()
        : { page: 0, limit: PAGINATION_LIMIT, q: '', sortBy: undefined },
    [allowSearchParam, readPagination]
  )

  const [data, setData] = useState<TData[]>([])
  const [count, setCount] = useState(0)
  const [selectedData, setSelectedData] = useState<DataTableSelectedData<TData, TExpanded>>(
    intialSelectedData ?? { isAllSelected: false, rows: [], expandedRows: [] }
  )
  const { modalState, toggleModal } = useModalManager(['action', 'form'])
  const [selectedAction, setSelectedAction] = useState<TableAction<TData, TExpanded>>()
  const [loading, setLoading] = useState(false)
  const [isSelectAllLoading, setIsSelectAllLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)
  const [search, setSearch] = useState<string>(q)

  const [paginationInput, setPaginationInput] = useState<PaginationInput>({
    pageIndex: page,
    pageSize: paginated ? limit : data.length,
    sorting: sortBy
      ? [{ id: sortBy.field, desc: sortBy.order === 'desc' }]
      : sorting
        ? [sorting]
        : []
  })
  const requestData: PaginationRequest<TFilters> = useMemo(
    () => ({
      search: search.trim(),
      page: paginationInput?.pageIndex,
      limit: paginationInput?.pageSize,
      sort: createSortingObject(paginationInput?.sorting ?? []),
      filter: memoizedFilters,
      version,
      ...memoizedExtraParams
    }),
    [paginationInput, search, memoizedFilters, memoizedExtraParams]
  )

  const { isFetching, refetch } = useQuery({
    queryKey: [endpoint, requestData],
    queryFn: async () => {
      const res = await restApiClient
        .url(encodeNestedObject<PaginationRequest<TFilters>>(endpoint, requestData))
        .get<PaginationResponse<TData>>()
      setData(res.data)
      setCount(res.total)

      return res.data
    },
    placeholderData: keepPreviousData,
    enabled: false
  })

  const isDeleteDisabled = (rows: TData[]) => !!permissions.disableDelete?.(rows)

  const deleteAction: TableAction<TData, TExpanded> = {
    type: 'multiple',
    icon: DeleteIcon,
    label: t('dynamic.data.table.delete.label'),
    cancelText: t('dynamic.data.table.delete.cancel'),
    variant: 'destructive',
    hasAccess: permissions.delete,
    requiredConfirmation: true,
    confirmationMessage: getDeleteMessage<TData>(selectedData.rows, { deleteMessage }),
    refetch: true,
    disabled: () =>
      selectedData.isAllSelected
        ? isDeleteDisabled(data)
        : selectedData.rows.length > 0 && isDeleteDisabled(selectedData.rows),
    fn: async (rows) => {
      if (!rows.length || isDeleteDisabled(rows)) return

      await restApiClient
        .url(encodeNestedObject(endpoint, { ids: rows.map((row) => row.id) }))
        .delete()

      await onDeleteSuccess?.(rows)

      setSelectedData({ isAllSelected: false, expandedRows: [], rows: [] })
      if (allowSearchParam) pushPagination({ ...paginationInput, pageIndex: 0, search })
      setPaginationInput((prev) => ({ ...prev, pageIndex: 0 }))
      toast.success(t('dynamic.data.table.delete.success'))
    }
  }

  const tableData = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        rowBorderColor: specialRows?.find((sr) => sr.condition(d) && sr.borderColor)?.borderColor,
        rowColor: specialRows?.find((sr) => sr.condition(d) && sr.color)?.color
      })),
    [data, specialRows]
  )

  const runAction = async (action: TableAction<TData, TExpanded>) => {
    if (selectedData.isAllSelected) {
      const allData = await restApiClient
        .url(
          encodeNestedObject<PaginationRequest<TFilters>>(endpoint, {
            ...requestData,
            limit: undefined,
            version: 'FULL'
          })
        )
        .get<PaginationResponse<TData>>()
      await action.fn?.(allData.data)
    } else {
      if (action.type === 'multiple')
        await action.fn?.(selectedData.rows, selectedData.expandedRows)
      if (action.type === 'global') await action.fn?.(selectedData.rows, selectedData.expandedRows)
    }
    if (action.successMessage) toast.success(action.successMessage)
  }
  useImperativeHandle(
    ref,
    () => ({
      refetch: () => {
        refetch()
        setSelectedData({ isAllSelected: false, rows: [], expandedRows: [] })
      },
      startSelectAllLoading: () => {
        setIsSelectAllLoading(true)
      },
      endSelectAllLoading: () => {
        setIsSelectAllLoading(false)
      },
      clearSelection: () => {
        setSelectedData({ isAllSelected: false, rows: [], expandedRows: [] })
      },
      setSelectedRows: (rows) => {
        setSelectedData({ isAllSelected: false, rows, expandedRows: [] })
      },
      currentData: data
    }),
    [data]
  )

  // When search is enabled, we want to wait for the user to stop typing before making the request
  // This is why we use a debounce of 300ms
  // We also want to reset the pageIndex to 0 when the user types something
  useUpdateEffect(() => {
    onSearchChange?.(search)

    const timer = setTimeout(() => {
      if (allowSearchParam) pushPagination({ ...paginationInput, pageIndex: 0, search })

      if (paginationInput.pageIndex !== 0)
        setPaginationInput((prev) => ({ ...prev, pageIndex: 0 }))
      else refetch()
    }, 300)

    return () => clearTimeout(timer)
  }, [search])

  // When filters change, we want to reset the pageIndex to 0
  useUpdateEffect(() => {
    if (paginationInput.pageIndex !== 0)
      // changing pageIndex will trigger the pagination effect which refetches
      setPaginationInput((prev) => ({ ...prev, pageIndex: 0 }))
    else
      // already on first page, so pagination effect won't fire → refetch manually
      refetch()
  }, [memoizedFilters])

  // When extraParams change (e.g. cseCode), treat them like filters
  useUpdateEffect(() => {
    if (paginationInput.pageIndex !== 0) setPaginationInput((prev) => ({ ...prev, pageIndex: 0 }))
    else refetch()
  }, [memoizedExtraParams])

  useEffect(() => {
    onSelectedDataChange?.(selectedData)
  }, [selectedData])

  // When page, limit, or sorting changes, refetch with appropriate timing
  useEffect(() => {
    // Immediate refetch for page/limit (users expect instant pagination)
    // Debounced refetch for sorting (prevent flickering from rapid clicks)
    const isSortingChange =
      JSON.stringify(paginationInput.sorting) !== JSON.stringify(sorting ? [sorting] : [])

    if (isSortingChange) {
      const timer = setTimeout(() => {
        refetch()
      }, 200) // Slightly longer debounce for better stability
      return () => clearTimeout(timer)
    } else {
      refetch()
    }
  }, [
    paginationInput.pageIndex,
    paginationInput.pageSize,
    JSON.stringify(paginationInput.sorting)
  ])

  useEffect(() => {
    onLoadingChange?.(isFetching)
  }, [isFetching])

  useEffect(() => {
    if (data && data.length) onDataChange?.(data)
  }, [data])

  return (
    <Box className={cn({ 'p-0': headerType === 'light' || headerType === 'none' }, className)}>
      {headerType !== 'none' && (
        <>
          {description && (
            <Typography variant="tag" className="mb-2">
              {description}
            </Typography>
          )}
          <div
            className={cn('flex justify-between items-center flex-wrap gap-2', {
              'py-2': headerType === 'full'
            })}
          >
            {headerType === 'full' && (
              <div className="flex items-center gap-4">
                <DynamicDataTableTitle>{title}</DynamicDataTableTitle>
                {label ? (
                  label(count)
                ) : (
                  <Badge text={`Total : ${count}`} variant="warning" size="medium" />
                )}
              </div>
            )}
            {headerType === 'hide-total' && <DynamicDataTableTitle>{title}</DynamicDataTableTitle>}

            {isSearchable && (
              <DynamicDataTableSearchInput
                label={searchLabel}
                value={search}
                headerType={headerType}
                onChange={setSearch}
              />
            )}
          </div>
          {headerInfo}
          <div
            className={cn('flex justify-between items-center gap-2', {
              'mt-4': headerType === 'full' || headerType === 'hide-total',
              'justify-end': headerType === 'minimal'
            })}
          >
            <div
              className={cn('flex items-center flex-wrap gap-2 ', {
                'w-9/12': !!filters && !!onFiltersClick,
                hidden: headerType === 'minimal'
              })}
            >
              {actionLabel}
              {[...actions, deleteAction]
                .filter((action) => action.hasAccess !== false)
                .map((action) => (
                  <Button
                    type="button"
                    key={action.label}
                    variant={action.variant}
                    icon={action.icon}
                    label={action.label}
                    loading={buttonLoading && selectedAction?.label === action.label}
                    disabled={
                      (typeof action.disabled === 'function'
                        ? action.disabled(selectedData.rows, selectedData.expandedRows, data)
                        : action.disabled) ||
                      (action.type === 'multiple' &&
                        !selectedData.isAllSelected &&
                        !selectedData.rows.length &&
                        !selectedData.expandedRows?.length)
                    }
                    onClick={async () => {
                      setSelectedAction(action)
                      if (action.requiredConfirmation) toggleModal('action')
                      else {
                        setButtonLoading(true)
                        try {
                          await runAction(action)
                          if (action.refetch) {
                            try {
                              const result = await refetch()
                              // Only reset selection if refetch succeeded (no error returned)
                              if (!result?.error) {
                                onSelectedDataChange?.({
                                  isAllSelected: false,
                                  rows: [],
                                  expandedRows: []
                                })
                              }
                            } catch {
                              // Ignore refetch failure; loading will stop in finally
                            }
                          }
                        } catch {
                          // Swallow action errors here; specific actions handle their own toasts
                        } finally {
                          setButtonLoading(false)
                        }
                      }
                    }}
                  />
                ))}
            </div>
            {postActions ? <div>{postActions}</div> : null}
            {onFiltersClick && (
              <Button
                type="button"
                variant={memoizedFilters && filtersLength ? 'highlighted' : 'secondary'}
                icon={FilterIcon}
                label={t('filters.title')}
                className="mobile:w-full tab:w-full"
                onClick={onFiltersClick}
              >
                <Counter number={filtersLength} />
              </Button>
            )}
          </div>
          <div className="h-4" />
        </>
      )}
      {widget && (
        <div className="mb-4">{typeof widget === 'function' ? widget(data) : widget}</div>
      )}
      <DataTable<TData, TExpanded>
        columns={columns}
        data={tableData}
        displayOrder={displayOrder}
        mode={mode as 'normal' & 'timeline'}
        overflow={overflow}
        tableClassName={tableClassName}
        expandedOptions={expandedOptions}
        isLoading={isFetching}
        isSelectAllLoading={isSelectAllLoading}
        rowCount={count}
        selectable={selectable && data.length > 0}
        disableSelectAll={disableSelectAll}
        selectMode={selectMode}
        legends={legends}
        disableHover={!onRowClick}
        paginated={paginated}
        bodyVariant={bodyVariant}
        state={paginationInput}
        limitControl={limitControl}
        onRowClick={onRowClick}
        disableRow={disableRow}
        footer={footer}
        footerCells={footerCells}
        footerRowClassName={footerclassName}
        selectedData={selectedData}
        onSelectedDataChange={setSelectedData}
        onStateChange={(newState) => {
          setPaginationInput((prev) => {
            const same =
              prev.pageIndex === newState.pageIndex &&
              prev.pageSize === newState.pageSize &&
              JSON.stringify(prev.sorting) === JSON.stringify(newState.sorting)

            if (!same && allowSearchParam) pushPagination({ ...newState, search })
            return same ? prev : newState
          })
        }}
      />

      <Modal
        actionButtonsOptions={{
          hideIcon: selectedAction?.hideButtonIcon,
          confirmText:
            selectedAction?.confirmText ??
            (selectedAction?.variant === 'destructive'
              ? t('dynamic.data.table.delete.label')
              : t('dynamic.data.table.delete.confirm')),
          cancelText: selectedAction?.cancelText,
          onConfirm: async () => {
            setLoading(true)
            try {
              if (selectedAction) await runAction(selectedAction)

              const shouldRefetch = !!selectedAction?.refetch
              let canClose = !shouldRefetch

              if (shouldRefetch) {
                const result = await refetch()
                canClose = !result?.error
                setSelectedData({ isAllSelected: false, rows: [], expandedRows: [] })
              }

              if (canClose) {
                toggleModal('action')
                setSelectedData({ isAllSelected: false, rows: [], expandedRows: [] })
              }
            } finally {
              setLoading(false)
            }
          }
        }}
        onCancel={() => {
          toggleModal('action')
          setSelectedData({ isAllSelected: false, rows: [], expandedRows: [] })
        }}
        isOpen={modalState.action}
        title={selectedAction?.confirmationMessage ?? ''}
        icon={
          selectedAction?.confirmationIcon ??
          (selectedAction?.variant === 'destructive' ? DeleteIcon : CheckMarkIcon)
        }
        isLoading={loading}
        variant={selectedAction?.variant === 'destructive' ? 'destructive' : 'confirmation'}
      />
    </Box>
  )
}) as DynamicDataTableComponent

export default DynamicDataTable
